<template>
  <div
    v-if="isAuthenticated && currentUser"
    class="spotify-widget authenticated"
  >
    <button
      class="spotify-connected-btn"
      :title="getButtonTitle()"
      @click="handleLogout"
    >
      <img
        v-if="currentUser.imageUrl"
        :src="currentUser.imageUrl"
        :alt="currentUser.displayName"
        class="spotify-user-avatar"
      >
      <span class="spotify-user-name">{{ currentUser.displayName }}</span>
      <span
        v-if="!playerInitialized"
        class="spotify-warning-icon"
        :title="getBrowserWarning()"
      >⚠</span>
    </button>
  </div>

  <div
    v-else
    class="spotify-widget unauthenticated"
  >
    <button
      class="spotify-login-btn"
      @click="handleLogin"
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
      >
        <path
          fill="currentColor"
          d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"
        />
      </svg>
      Connect Spotify
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import type { SpotifyService, SpotifyUser } from '../services/SpotifyService';
import { logger } from '@maso-dj/shared-types';

interface Props {
  spotifyService: SpotifyService;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  authChange: [authenticated: boolean];
}>();

const isAuthenticated = ref(false);
const currentUser = ref<SpotifyUser | null>(null);
const playerInitialized = ref(false);
const isBraveBrowser = ref(false);

const getBrowserWarning = (): string => {
  if (isBraveBrowser.value) {
    return 'Using 30-second previews. Brave browser: Open brave://settings/extensions and enable "Google Widevine", then reload. Or use Chrome/Edge for full playback.';
  }
  return 'Using 30-second previews. Full playback requires Spotify Premium and DRM-enabled browser (Chrome/Edge work best).';
};

const getButtonTitle = (): string => {
  if (!playerInitialized.value) {
    return `${getBrowserWarning()}\n\nClick to disconnect from Spotify`;
  }
  return 'Click to disconnect from Spotify';
};

const checkAuthStatus = async (): Promise<void> => {
  logger.debug('Checking Spotify auth status...');
  isAuthenticated.value = await props.spotifyService.isAuthenticated();
  logger.debug('Spotify authenticated:', isAuthenticated.value);

  if (isAuthenticated.value) {
    currentUser.value = await props.spotifyService.getCurrentUser();
    logger.debug('Spotify user:', currentUser.value);

    // Check if player device is initialized
    playerInitialized.value = props.spotifyService.getDeviceId() !== null;
    logger.debug('Spotify player device initialized:', playerInitialized.value);
  }

  logger.debug('Emitting authChange event with authenticated:', isAuthenticated.value);
  emit('authChange', isAuthenticated.value);
};

const handleLogin = (): void => {
  props.spotifyService.login();
};

const handleLogout = async (): Promise<void> => {
  await props.spotifyService.logout();
  isAuthenticated.value = false;
  currentUser.value = null;

  emit('authChange', false);
};

const refresh = async (): Promise<void> => {
  await checkAuthStatus();
};

const handleSpotifyAuthComplete = async (event?: Event): Promise<void> => {
  logger.debug('Spotify auth complete event received');

  // Check if event has error details
  const customEvent = event as CustomEvent;
  if (customEvent?.detail?.success === false) {
    logger.error('Spotify auth failed:', customEvent.detail.error);
    return;
  }

  // Wait a bit for session to be saved on backend
  await new Promise(resolve => setTimeout(resolve, 500));

  logger.debug('Refreshing auth status...');
  await refresh();
};

const handlePlayerReady = (): void => {
  logger.debug('Spotify player ready - updating widget status');
  playerInitialized.value = true;
};

// Expose refresh method to parent component
defineExpose({
  refresh
});

onMounted(() => {
  // Detect Brave browser
  interface NavigatorWithBrave extends Navigator {
    brave?: {
      isBrave: () => boolean;
    };
  }
  isBraveBrowser.value = !!(navigator as NavigatorWithBrave).brave && typeof (navigator as NavigatorWithBrave).brave.isBrave === 'function';

  checkAuthStatus();
  // Listen for popup auth completion
  window.addEventListener('spotify-auth-complete', handleSpotifyAuthComplete);

  // Listen for player ready event to update UI
  props.spotifyService.onPlayerReady(handlePlayerReady);
});

onUnmounted(() => {
  window.removeEventListener('spotify-auth-complete', handleSpotifyAuthComplete);
});
</script>
